import { Outlet, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  Star as StarIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import NavSearch from './NavSearch';
import VerifyBanner from './VerifyBanner';
import { tokens } from '../theme';

export default function Layout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuthStore();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // The homepage's hero video takes the full viewport; the nav sits
  // transparently on top of it.
  const isHome = location.pathname === '/';
  // The admin page hosts wide data tables (Users, Ratings, etc.) that
  // overflow the standard `lg` container and force horizontal scroll.
  // Bump it to `xl` so those tables get more breathing room.
  const isAdmin = location.pathname.startsWith('/admin');

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { label: 'Top Rated', path: '/top-rated', icon: <StarIcon /> },
  ];

  const drawer = (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Barlow Condensed", sans-serif',
            fontWeight: 900,
            fontSize: 22,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            lineHeight: 1,
          }}
        >
          Dynasty<Box component="span" sx={{ color: tokens.orange }}>Rep</Box>
        </Typography>
        <BetaTag />
      </Box>
      {/* No search input here — the persistent mobile search row in
          the AppBar covers that need. The drawer stays focused on
          navigation links. */}
      <Divider />
      <List onClick={handleDrawerToggle}>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton component={RouterLink} to={item.path}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        {isAuthenticated && (
          <>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/dashboard">
                <ListItemIcon>
                  <DashboardIcon />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            </ListItem>
            {user?.isAdmin && (
              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/admin">
                  <ListItemIcon>
                    <AdminIcon color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Admin" />
                </ListItemButton>
              </ListItem>
            )}
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position={isHome ? 'absolute' : 'sticky'}
        elevation={0}
        sx={
          isHome
            ? {
                // Transparent gradient over the hero video
                backgroundColor: 'transparent',
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)',
                borderBottom: 'none',
                boxShadow: 'none',
              }
            : {
                backgroundColor: tokens.bg,
                borderBottom: `1px solid ${tokens.border}`,
              }
        }
      >
        <Toolbar sx={{ minHeight: { xs: 60, md: 68 }, px: { xs: 1.5, md: 5 } }}>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.75, md: 1.25 },
              textDecoration: 'none',
              color: 'inherit',
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Barlow Condensed", sans-serif',
                fontWeight: 900,
                // Tighten the wordmark slightly on small screens so the
                // header can comfortably hold the auth buttons next to it.
                fontSize: { xs: 22, md: 28 },
                letterSpacing: { xs: '1px', md: '1.5px' },
                textTransform: 'uppercase',
                color: tokens.text,
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              Dynasty<Box component="span" sx={{ color: tokens.orange }}>Rep</Box>
            </Typography>
            <BetaTag />
          </Box>

          {!isMobile && (
            <Box sx={{ ml: 4, display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: 13,
                    fontWeight: 500,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    color: 'rgba(240,236,227,0.65)',
                    padding: '6px 14px',
                    '&:hover': {
                      color: tokens.text,
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              {/* Search sits right of the nav items on every page. */}
              <Box sx={{ ml: 1 }}>
                <NavSearch />
              </Box>
            </Box>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {isAuthenticated ? (
            <>
              {!isMobile && (
                <Button
                  component={RouterLink}
                  to="/dashboard"
                  startIcon={<DashboardIcon />}
                  sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: 13,
                    fontWeight: 500,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    color: 'rgba(240,236,227,0.7)',
                    mr: 1,
                    '&:hover': { color: tokens.text, backgroundColor: 'transparent' },
                  }}
                >
                  Dashboard
                </Button>
              )}
              <IconButton onClick={handleMenu} color="inherit">
                <Avatar
                  src={user?.avatarUrl || undefined}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: tokens.border2,
                    color: tokens.text2,
                    fontFamily: '"Barlow Condensed", sans-serif',
                    fontWeight: 700,
                    border: `1.5px solid ${tokens.border}`,
                  }}
                >
                  {user?.displayName?.[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>
                </MenuItem>
                <Divider />
                <MenuItem component={RouterLink} to="/dashboard" onClick={handleClose}>
                  <ListItemIcon>
                    <DashboardIcon fontSize="small" />
                  </ListItemIcon>
                  Dashboard
                </MenuItem>
                {user?.isAdmin && (
                  <MenuItem
                    component={RouterLink}
                    to="/admin"
                    onClick={handleClose}
                  >
                    <ListItemIcon>
                      <AdminIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    Admin Dashboard
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            // Auth-entry CTAs. Sized down on mobile so they fit alongside
            // the brand wordmark without wrapping the whole AppBar onto a
            // second line on narrow phones.
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 0.75, md: 1 },
                flexShrink: 0,
              }}
            >
              <Button
                component={RouterLink}
                to="/login"
                variant="outlined"
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: { xs: 11, md: 13 },
                  fontWeight: 500,
                  letterSpacing: '0.3px',
                  textTransform: 'uppercase',
                  padding: { xs: '5px 10px', md: '8px 18px' },
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  color: 'rgba(240,236,227,0.7)',
                  borderColor: 'rgba(255,255,255,0.18)',
                  '&:hover': {
                    color: tokens.text,
                    borderColor: 'rgba(255,255,255,0.4)',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Log In
              </Button>
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                color="primary"
                sx={{
                  fontSize: { xs: 12, md: 15 },
                  padding: { xs: '6px 12px', md: '9px 22px' },
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                Sign Up
              </Button>
            </Box>
          )}
        </Toolbar>

        {/* Persistent mobile search row. Search is the site's primary
            action — burying it in the hamburger drawer makes the whole
            value prop hard to find on a phone. The orange-accented
            border makes it visually pop without competing with the
            wordmark in the row above. Hidden at md+ where search lives
            in the toolbar itself. */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            px: 1.5,
            pt: 0.5,
            pb: 1.5,
            borderTop: `1px solid rgba(255,255,255,0.06)`,
          }}
        >
          <Box
            sx={{
              borderLeft: `2px solid ${tokens.orange}`,
              pl: 1,
            }}
          >
            <NavSearch />
          </Box>
        </Box>
      </AppBar>

      {/* Verification reminder. The component itself decides whether to
          render based on auth state and current route — keep this mount
          point unconditional so route changes re-evaluate it. */}
      <VerifyBanner />

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 240,
            bgcolor: 'background.paper',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content. The homepage renders full-bleed (its hero spans the
          entire viewport), so skip the Container there. The admin page
          uses `xl` to give its tables more horizontal room. */}
      <Box component="main" sx={{ flexGrow: 1, width: '100%' }}>
        {isHome ? (
          <Outlet />
        ) : (
          <Container maxWidth={isAdmin ? 'xl' : 'lg'} sx={{ py: 4 }}>
            <Outlet />
          </Container>
        )}
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ fontFamily: '"DM Sans", sans-serif' }}
          >
            © {new Date().getFullYear()} DynastyRep · Building trust in dynasty fantasy football.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

/**
 * "Beta" pill that sits next to the DynastyRep wordmark. Signals the app is
 * pre-launch. Gold outline + gold text so it reads clearly over both the
 * transparent homepage nav (dark video) and the solid nav on other pages.
 */
function BetaTag() {
  return (
    <Box
      sx={{
        fontFamily: '"Barlow Condensed", sans-serif',
        fontWeight: 700,
        fontSize: 10,
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        color: tokens.gold,
        border: `1px solid ${tokens.gold}`,
        padding: '2px 7px',
        lineHeight: 1.2,
        backgroundColor: 'rgba(201, 160, 62, 0.08)',
        // Pair visually with the uppercase wordmark next to it.
        position: 'relative',
        top: -1,
      }}
    >
      Beta
    </Box>
  );
}
